import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, MapPin, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageSpinner } from '@/components/ui/Spinner';
import { locationsApi, type Location } from '@/api/endpoints/locations';

type Mode = 'list' | 'create' | 'edit';

interface LocationFormData {
  name: string;
  slug: string;
  type: 'city' | 'neighborhood' | 'district';
  state: string;
  latitude: string;
  longitude: string;
  description: string;
  isActive: boolean;
}

const emptyForm: LocationFormData = {
  name: '',
  slug: '',
  type: 'city',
  state: '',
  latitude: '',
  longitude: '',
  description: '',
  isActive: true,
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function LocationsPage() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('list');
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [form, setForm] = useState<LocationFormData>(emptyForm);
  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('');

  // Fetch locations
  const { data, isLoading } = useQuery({
    queryKey: ['admin-locations', filterType, filterActive],
    queryFn: () => locationsApi.list({
      type: filterType || undefined,
      activeOnly: filterActive === 'true' ? true : undefined,
      limit: 500,
    }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: locationsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success('Location created');
      resetForm();
    },
    onError: () => {
      toast.error('Failed to create location');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Location> }) =>
      locationsApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success('Location updated');
      resetForm();
    },
    onError: () => {
      toast.error('Failed to update location');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: locationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success('Location deleted');
    },
    onError: () => {
      toast.error('Failed to delete location');
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      locationsApi.update(id, { isActive }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success(variables.isActive ? 'Location activated' : 'Location deactivated');
    },
    onError: () => {
      toast.error('Failed to update location');
    },
  });

  const locations = data?.locations || [];

  function resetForm() {
    setMode('list');
    setEditingLocation(null);
    setForm(emptyForm);
  }

  function handleCreate() {
    setMode('create');
    setForm(emptyForm);
  }

  function handleEdit(location: Location) {
    setMode('edit');
    setEditingLocation(location);
    setForm({
      name: location.name,
      slug: location.slug,
      type: location.type,
      state: location.state || '',
      latitude: String(location.latitude),
      longitude: String(location.longitude),
      description: location.description || '',
      isActive: location.isActive,
    });
  }

  function handleNameChange(name: string) {
    setForm(prev => ({
      ...prev,
      name,
      // Auto-generate slug from name if creating new or slug hasn't been manually edited
      slug: mode === 'create' || prev.slug === slugify(prev.name)
        ? slugify(name)
        : prev.slug,
    }));
  }

  function handleSubmit() {
    if (!form.name || !form.slug || !form.latitude || !form.longitude) {
      toast.error('Please fill in all required fields');
      return;
    }

    const locationData = {
      name: form.name,
      slug: form.slug,
      type: form.type,
      state: form.state || null,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      description: form.description || null,
      isActive: form.isActive,
    };

    if (mode === 'create') {
      createMutation.mutate(locationData as Parameters<typeof locationsApi.create>[0]);
    } else if (mode === 'edit' && editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, updates: locationData });
    }
  }

  function handleDelete(location: Location) {
    if (confirm(`Delete "${location.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(location.id);
    }
  }

  function handleToggleActive(location: Location) {
    toggleActiveMutation.mutate({ id: location.id, isActive: !location.isActive });
  }

  if (isLoading) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Locations</h1>
          <p className="text-[var(--color-muted)]">
            Manage city and neighborhood landing pages for SEO
          </p>
        </div>
        {mode === 'list' && (
          <Button variant="primary" onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {(mode === 'create' || mode === 'edit') && (
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === 'create' ? 'Create Location' : `Edit ${editingLocation?.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  placeholder="Kansas City"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug *</label>
                <Input
                  placeholder="kansas-city"
                  value={form.slug}
                  onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
                  value={form.type}
                  onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value as LocationFormData['type'] }))}
                >
                  <option value="city">City</option>
                  <option value="neighborhood">Neighborhood</option>
                  <option value="district">District</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <Input
                  placeholder="MO"
                  value={form.state}
                  onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))}
                  maxLength={2}
                />
                <p className="text-xs text-[var(--color-muted)] mt-1">
                  2-letter state code (for cities)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Latitude *</label>
                <Input
                  placeholder="39.0997"
                  value={form.latitude}
                  onChange={(e) => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                  type="number"
                  step="any"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Longitude *</label>
                <Input
                  placeholder="-94.5786"
                  value={form.longitude}
                  onChange={(e) => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                  type="number"
                  step="any"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm min-h-[80px]"
                placeholder="SEO description for this location..."
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={form.isActive}
                onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                className="rounded border-[var(--color-border)]"
              />
              <label htmlFor="isActive" className="text-sm">
                Active (visible on site)
              </label>
            </div>

            <div className="flex gap-3 pt-4 border-t border-[var(--color-border)]">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : mode === 'create'
                    ? 'Create Location'
                    : 'Save Changes'}
              </Button>
              <Button variant="ghost" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {mode === 'list' && (
        <div className="flex gap-4 flex-wrap">
          <select
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="city">City</option>
            <option value="neighborhood">Neighborhood</option>
            <option value="district">District</option>
          </select>
          <select
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active Only</option>
            <option value="false">All (including inactive)</option>
          </select>
          <div className="text-sm text-[var(--color-muted)] flex items-center">
            {locations.length} location{locations.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Location List */}
      {mode === 'list' && (
        <div className="grid gap-4">
          {locations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MapPin className="w-12 h-12 mx-auto text-[var(--color-muted)] mb-4" />
                <p className="text-[var(--color-muted)]">No locations found</p>
                <p className="text-sm text-[var(--color-muted)] mt-1">
                  Run the discover-locations script or add locations manually
                </p>
              </CardContent>
            </Card>
          ) : (
            locations.map((location) => (
              <Card key={location.id} className={!location.isActive ? 'opacity-60' : ''}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">
                          {location.name}
                          {location.state && `, ${location.state}`}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                          location.type === 'city'
                            ? 'bg-blue-500/20 text-blue-400'
                            : location.type === 'neighborhood'
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {location.type}
                        </span>
                        {location.isActive ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[var(--color-muted)] mt-1">
                        <code className="text-xs bg-[var(--color-border)] px-1.5 py-0.5 rounded">
                          /city/{location.slug}
                        </code>
                        <span className="mx-2">·</span>
                        {Number(location.latitude).toFixed(4)}, {Number(location.longitude).toFixed(4)}
                      </div>
                      {location.description && (
                        <p className="text-sm text-[var(--color-muted)] mt-2 line-clamp-2">
                          {location.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(location)}
                        title={location.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {location.isActive ? (
                          <X className="w-4 h-4 text-red-400" />
                        ) : (
                          <Check className="w-4 h-4 text-green-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(location)}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(location)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Info Card */}
      {mode === 'list' && (
        <Card className="border-blue-500/30">
          <CardContent className="py-4">
            <h4 className="font-medium text-blue-400 mb-2">Auto-Discovery</h4>
            <p className="text-sm text-[var(--color-muted)]">
              To auto-discover cities from existing bar addresses, run:
            </p>
            <code className="block mt-2 text-xs bg-[var(--color-border)] px-3 py-2 rounded">
              pnpm --filter @ml4/backend run discover-locations
            </code>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
